/**
 * Mega Menu - Desktop interactions and content box rotation
 *
 * Handles:
 * - Desktop hover interactions with delay
 * - Content box rotation with cookie persistence (24h cycle)
 * - Keyboard navigation and accessibility
 * - Integration with NavigationV2 for mobile
 */

class MegaMenu {
  constructor() {
    this.nav = document.querySelector(".nav-v2[data-mega-menu-enabled]");
    if (!this.nav) return;

    // Prevent double initialization when imported from multiple bundles
    if (this.nav.dataset.megaMenuInitialized) return;
    this.nav.dataset.megaMenuInitialized = 'true';

    // Cookie settings for rotation
    this.ROTATION_COOKIE_NAME = "psm_mega_menu_rotation";

    // Hover delay settings (ms)
    this.HOVER_OPEN_DELAY = 100;
    this.HOVER_CLOSE_DELAY = 200;

    // State tracking
    this.openPanel = null;
    this.hoverTimeout = null;
    this.closeTimeout = null;

    // Track calculated indices for this page load (prevents double-rotation for desktop/mobile)
    this.pageLoadIndices = {};

    this.init();
  }

  init() {
    this.initDesktopPanels();
    this.initPanelPositioning();
    this.initContentBoxRotation();
    this.initKeyboardNav();
    this.initVideoModal();
  }

  /**
   * Initialize desktop mega menu panel interactions
   */
  initDesktopPanels() {
    const panels = this.nav.querySelectorAll("[data-mega-menu-panel]");

    // The panels are already handled by CSS hover states (group-hover)
    // We just need to handle focus/keyboard and some edge cases

    panels.forEach((panel) => {
      // Keep panel open when hovering the panel itself
      panel.addEventListener("mouseenter", () => {
        this.clearCloseTimeout();
      });

      panel.addEventListener("mouseleave", () => {
        // Let the CSS handle the visibility
      });
    });

    // Handle focus for accessibility
    const menuLinks = this.nav.querySelectorAll("nav ul > li > a");
    menuLinks.forEach((link) => {
      const panel = link.closest("li")?.querySelector("[data-mega-menu-panel]");

      if (panel) {
        link.addEventListener("focus", () => {
          // Only show panel on keyboard focus (tab), not mouse click
          if (link.matches(":focus-visible")) {
            this.showPanel(panel);
          }
        });

        let focusOutTimeout;
        const hideIfFocusOutside = () => {
          clearTimeout(focusOutTimeout);
          focusOutTimeout = setTimeout(() => {
            if (
              !panel.contains(document.activeElement) &&
              !link.contains(document.activeElement)
            ) {
              this.hidePanel(panel);
            }
          }, 100);
        };

        link.addEventListener("blur", hideIfFocusOutside);
        panel.addEventListener("focusout", hideIfFocusOutside);
      }
    });
  }

  /**
   * Initialize panel positioning to keep panels within container bounds
   */
  initPanelPositioning() {
    const menuItems = this.nav.querySelectorAll("li.group");

    menuItems.forEach((menuItem) => {
      const trigger = menuItem.querySelector("a[aria-haspopup]");
      const panel = menuItem.querySelector(".mega-menu-panel");

      if (!trigger || !panel) return;

      // Position panel on mouseenter (with slight delay to ensure panel is visible)
      menuItem.addEventListener("mouseenter", () => {
        requestAnimationFrame(() => {
          this.positionPanel(trigger, panel);
        });
      });

      // Also position on focus for keyboard users
      trigger.addEventListener("focus", () => {
        this.positionPanel(trigger, panel);
      });
    });

    // Reposition on window resize
    let resizeTimeout;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const visiblePanel = this.nav.querySelector(
          ".mega-menu-panel:not(.invisible)",
        );
        if (visiblePanel) {
          const menuItem = visiblePanel.closest("li.group");
          const trigger = menuItem?.querySelector("a[aria-haspopup]");
          if (trigger) {
            this.positionPanel(trigger, visiblePanel);
          }
        }
      }, 100);
    });

    // Check if mouse is already hovering on page load
    // (mouseenter won't fire if mouse is already there when JS initializes)
    // Use setTimeout to let the layout fully settle before positioning
    setTimeout(() => {
      menuItems.forEach((menuItem) => {
        if (menuItem.matches(":hover")) {
          const trigger = menuItem.querySelector("a[aria-haspopup]");
          const panel = menuItem.querySelector(".mega-menu-panel");
          if (trigger && panel) {
            this.positionPanel(trigger, panel);
          }
        }
      });
    }, 50);
  }

  /**
   * Position a panel centered under trigger, constrained within nav wrapper bounds
   * Uses fixed positioning with viewport coordinates
   */
  positionPanel(trigger, panel) {
    const triggerRect = trigger.getBoundingClientRect();
    const navWrapper = this.nav.querySelector("[data-nav-wrapper]");
    if (!navWrapper) return;

    const wrapperRect = navWrapper.getBoundingClientRect();

    // Position below trigger with gap
    const top = triggerRect.bottom + 8;
    panel.style.top = `${top}px`;

    // Get panel width (offsetWidth is unaffected by transforms)
    const panelWidth = panel.offsetWidth;

    // Center under trigger
    const triggerCenter = triggerRect.left + triggerRect.width / 2;
    let left = triggerCenter - panelWidth / 2;

    // Constrain to wrapper bounds
    left = Math.max(wrapperRect.left, Math.min(left, wrapperRect.right - panelWidth));

    panel.style.left = `${left}px`;

    // Position the hover bridge to only cover the trigger element
    const bridgeLeft = triggerRect.left - left;
    panel.style.setProperty("--bridge-left", `${bridgeLeft}px`);
    panel.style.setProperty("--bridge-width", `${triggerRect.width}px`);
  }

  /**
   * Show a mega menu panel (for focus/keyboard)
   */
  showPanel(panel) {
    panel.classList.remove(
      "invisible",
      "opacity-0",
      "scale-95",
      "-translate-y-1",
    );
    panel.classList.add("visible", "opacity-100", "scale-100", "translate-y-0");
  }

  /**
   * Hide a mega menu panel (for focus/keyboard)
   */
  hidePanel(panel) {
    panel.classList.add("invisible", "opacity-0", "scale-95", "-translate-y-1");
    panel.classList.remove(
      "visible",
      "opacity-100",
      "scale-100",
      "translate-y-0",
    );
  }

  /**
   * Clear the close timeout
   */
  clearCloseTimeout() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  }

  /**
   * Initialize content box rotation
   */
  initContentBoxRotation() {
    const rotatingContainers = document.querySelectorAll(
      "[data-mega-menu-content-rotation]",
    );

    rotatingContainers.forEach((container) => {
      const menuIdentifier = container.dataset.menuIdentifier;
      const boxes = container.querySelectorAll("[data-content-box-index]");

      if (boxes.length <= 1) return; // No rotation needed

      // Get per-panel rotation settings
      const rotationType = container.dataset.rotationType || "session";
      const rotationHours = parseInt(container.dataset.rotationHours, 10) || 24;

      // Get current rotation index from cookie
      const rotationData = this.getRotationData();
      const currentIndex = this.getCurrentBoxIndex(
        menuIdentifier,
        boxes.length,
        rotationData,
        rotationType,
        rotationHours,
      );

      // Show the correct box
      boxes.forEach((box, index) => {
        if (index === currentIndex) {
          box.classList.remove("hidden");
        } else {
          box.classList.add("hidden");
        }
      });
    });
  }

  /**
   * Get rotation data from cookie
   */
  getRotationData() {
    const cookie = this.getCookie(this.ROTATION_COOKIE_NAME);
    if (!cookie) return {};

    try {
      return JSON.parse(cookie);
    } catch {
      return {};
    }
  }

  /**
   * Get current box index for a menu, handling rotation
   *
   * @param {string} menuIdentifier - Unique identifier for the menu
   * @param {number} totalBoxes - Total number of content boxes
   * @param {object} rotationData - Current rotation data from cookie
   * @param {string} rotationType - 'session' or 'time'
   * @param {number} rotationHours - Hours for time-based rotation
   *
   * Rotation behavior:
   * - Session-based: rotates on each new browser session (new visit)
   * - Time-based: rotates after specified hours have passed
   */
  getCurrentBoxIndex(
    menuIdentifier,
    totalBoxes,
    rotationData,
    rotationType,
    rotationHours,
  ) {
    // If we already calculated this menu's index for this page load, return cached value
    // (prevents double-rotation when both desktop and mobile menus exist)
    if (this.pageLoadIndices[menuIdentifier] !== undefined) {
      return this.pageLoadIndices[menuIdentifier] % totalBoxes;
    }

    const now = Date.now();
    const menuData = rotationData[menuIdentifier];

    // Check if we have valid rotation data for this menu
    if (menuData && menuData.timestamp && menuData.index !== undefined) {
      let shouldRotate = false;

      if (rotationType === "session") {
        // Page-load based: rotate on every page load
        shouldRotate = true;
      } else if (rotationType === "time") {
        // Time-based: rotate when hours threshold passed
        const hoursSinceLastUpdate =
          (now - menuData.timestamp) / (1000 * 60 * 60);
        shouldRotate = hoursSinceLastUpdate >= rotationHours;
      }

      if (shouldRotate) {
        const newIndex = (menuData.index + 1) % totalBoxes;
        this.updateRotationData(menuIdentifier, newIndex);
        this.pageLoadIndices[menuIdentifier] = newIndex;
        return newIndex;
      }

      // No rotation needed - keep same index
      this.pageLoadIndices[menuIdentifier] = menuData.index;
      return menuData.index % totalBoxes;
    }

    // No existing data - start at 0
    this.updateRotationData(menuIdentifier, 0);
    this.pageLoadIndices[menuIdentifier] = 0;
    return 0;
  }

  /**
   * Update rotation data in cookie
   */
  updateRotationData(menuIdentifier, index) {
    const rotationData = this.getRotationData();

    rotationData[menuIdentifier] = {
      index: index,
      timestamp: Date.now(),
    };

    // Set cookie with 30-day expiry (rotation interval is internal logic)
    this.setCookie(this.ROTATION_COOKIE_NAME, JSON.stringify(rotationData), 30);
  }

  /**
   * Initialize keyboard navigation
   */
  initKeyboardNav() {
    // Handle Escape key to close panels
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        const openPanels = document.querySelectorAll(
          ".mega-menu-panel:not(.invisible)",
        );
        openPanels.forEach((panel) => this.hidePanel(panel));

        // Return focus to the menu item
        const focusedElement = document.activeElement;
        if (focusedElement && focusedElement.closest(".mega-menu-panel")) {
          const menuItem = focusedElement.closest("li")?.querySelector("a");
          if (menuItem) menuItem.focus();
        }
      }
    });

    // Handle arrow key navigation within panels
    const panels = document.querySelectorAll("[data-mega-menu-panel]");
    panels.forEach((panel) => {
      const focusableElements = panel.querySelectorAll("a, button");

      panel.addEventListener("keydown", (e) => {
        if (
          !["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(e.key)
        )
          return;

        const currentIndex = Array.from(focusableElements).indexOf(
          document.activeElement,
        );
        if (currentIndex === -1) return;

        e.preventDefault();

        let nextIndex;
        switch (e.key) {
          case "ArrowDown":
          case "ArrowRight":
            nextIndex = (currentIndex + 1) % focusableElements.length;
            break;
          case "ArrowUp":
          case "ArrowLeft":
            nextIndex =
              (currentIndex - 1 + focusableElements.length) %
              focusableElements.length;
            break;
        }

        focusableElements[nextIndex]?.focus();
      });
    });
  }

  /**
   * Initialize the video modal used by video content boxes
   *
   * The YouTube iframe is only created when a box is clicked and removed
   * again on close, so nothing loads from YouTube until the user asks for it.
   */
  initVideoModal() {
    this.videoModal = document.querySelector("[data-video-modal]");
    if (!this.videoModal) return;

    this.videoPlayer = this.videoModal.querySelector(
      "[data-video-modal-player]",
    );
    this.videoTrigger = null;
    this.previousBodyOverflow = "";

    document.addEventListener("click", (e) => {
      const trigger = e.target.closest("[data-video-trigger]");
      if (trigger) {
        e.preventDefault();
        this.openVideoModal(trigger);
        return;
      }

      if (e.target.closest("[data-video-modal-close]")) {
        this.closeVideoModal();
      }
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isVideoModalOpen()) {
        this.closeVideoModal();
      }
    });
  }

  isVideoModalOpen() {
    return this.videoModal && !this.videoModal.classList.contains("hidden");
  }

  openVideoModal(trigger) {
    const { videoId, videoUrl, videoPoster, videoTitle, menuIdentifier } =
      trigger.dataset;
    if (!videoId && !videoUrl) return;

    const title = videoTitle || "Video";
    let player;

    if (videoUrl) {
      // Self-hosted file (media library)
      player = document.createElement("video");
      player.src = videoUrl;
      player.controls = true;
      player.autoplay = true;
      player.playsInline = true;
      if (videoPoster) player.poster = videoPoster;
      player.setAttribute("aria-label", title);
    } else {
      player = document.createElement("iframe");
      player.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?autoplay=1&rel=0`;
      player.title = title;
      player.allow =
        "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture";
      player.allowFullscreen = true;
    }

    player.style.width = "100%";
    player.style.height = "100%";
    player.style.border = "0";

    this.videoPlayer.replaceChildren(player);

    // Start self-hosted playback explicitly; if the browser blocks it the controls remain
    if (videoUrl) {
      player.play().catch(() => {});
    }
    this.videoModal.classList.remove("hidden");
    this.videoModal.classList.add("flex");
    this.videoModal.setAttribute("aria-hidden", "false");
    this.videoModal.setAttribute("aria-label", title);

    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    this.videoTrigger = trigger;
    this.videoModal.querySelector("button[data-video-modal-close]")?.focus();

    this.trackVideoOpen({
      videoId: videoId || videoUrl,
      videoType: videoUrl ? "file" : "youtube",
      videoTitle: title,
      menuIdentifier,
    });
  }

  closeVideoModal() {
    if (!this.isVideoModalOpen()) return;

    // Removing the player stops playback
    this.videoPlayer.replaceChildren();
    this.videoModal.classList.add("hidden");
    this.videoModal.classList.remove("flex");
    this.videoModal.setAttribute("aria-hidden", "true");

    document.body.style.overflow = this.previousBodyOverflow;

    this.videoTrigger?.focus();
    this.videoTrigger = null;
  }

  /**
   * Send a GA4 event when a video content box is opened
   */
  trackVideoOpen({ videoId, videoType, videoTitle, menuIdentifier }) {
    if (typeof window.gtag !== "function") return;

    window.gtag("event", "mega_menu_video_open", {
      video_id: videoId,
      video_type: videoType,
      video_title: videoTitle,
      menu_identifier: menuIdentifier || "",
    });
  }

  /**
   * Cookie utilities
   */
  getCookie(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === " ") c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  setCookie(name, value, days) {
    let expires = "";
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = "; expires=" + date.toUTCString();
    }
    document.cookie =
      name + "=" + (value || "") + expires + "; path=/; SameSite=Lax";
  }
}

// Initialize on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => new MegaMenu());
} else {
  new MegaMenu();
}

export default MegaMenu;
